import { useState, useCallback, useEffect } from "react";

/**
 * Custom hook to manage action card selection, execution, drawing, and discard & redraw
 */
export const useActionCardHandlers = (socket, room, name, actionsRemaining, myTurn, playerActionCards, maxActionCards, showMessage, addNotification) => {
  const [selectedActionCard, setSelectedActionCard] = useState(null);
  const [actionMode, setActionMode] = useState(null); // "selecting_stock_up", "selecting_stock_down", null
  const [redrawMode, setRedrawMode] = useState(false);
  const [selectedForDiscard, setSelectedForDiscard] = useState(new Set());
  const [hasUsedRedrawThisTurn, setHasUsedRedrawThisTurn] = useState(false);
  const [pendingTransferSource, setPendingTransferSource] = useState(null);

  // Reset redraw state when turn changes; clear transfer step when mode changes
  useEffect(() => {
    if (myTurn) {
      setHasUsedRedrawThisTurn(false);
    }
    setRedrawMode(false);
    setSelectedForDiscard(new Set());
  }, [myTurn]);

  useEffect(() => {
    if (actionMode !== "selecting_transfer_source" && actionMode !== "selecting_transfer_target") {
      setPendingTransferSource(null);
    }
  }, [actionMode]);

  const executeActionCard = (actionCard, target = null, direction = null) => {
    socket.current.emit("play_action_card", {
      room: room,
      playerName: name,
      cardId: actionCard.id,
      target: target,
      direction: direction
    });
  };

  const handleSelectActionCard = (actionCard) => {
    if (redrawMode) return;
    const myActions = actionsRemaining[name] || 0;
    if (!myTurn || myActions <= 0) return;
    
    if (selectedActionCard?.id === actionCard.id) {
      setSelectedActionCard(null);
      setActionMode(null);
    } else {
      setSelectedActionCard(actionCard);
      
      if (actionCard.targetType === "stock") {
        if (actionCard.actionType === "manipulate" || actionCard.actionType === "delayed_manipulate") {
          const isPositive = actionCard.effectValue > 0;
          const isDelayed = actionCard.actionType === "delayed_manipulate";
          const timingText = isDelayed ? " at start of next round" : "";
          
          if (isPositive) {
            setActionMode("selecting_stock_up");
            addNotification(`Select a stock to increase its price by ${actionCard.effectValue}${timingText}`, null);
          } else {
            setActionMode("selecting_stock_down");
            addNotification(`Select a stock to decrease its price by ${Math.abs(actionCard.effectValue)}${timingText}`, null);
          }
        } else if (actionCard.actionType === "remove_manipulators") {
          setActionMode("selecting_stock_remove");
          addNotification(`Select a stock - all manipulator cards will be removed at end of round`, null);
        } else if (actionCard.actionType === "trading_halt") {
          setActionMode("selecting_stock_ban");
          addNotification(`Select a stock to halt trading for the rest of this round`, null);
        } else if (actionCard.actionType === "rug_pull") {
          setActionMode("selecting_rug_pull");
          addNotification(`Select a stock or index you own to sell all your holdings of that specific stock/index`, null);
        } else if (actionCard.actionType === "double_down") {
          setActionMode("selecting_double_down");
          addNotification(`Select a stock you own to buy one more share at a discount (your shares × growth)`, null);
        }
      } else if (actionCard.targetType === "stock_pair" || actionCard.actionType === "transfer_manipulators") {
        setPendingTransferSource(null);
        setActionMode("selecting_transfer_source");
        addNotification(`Select source stock (with delayed manipulators; cannot be under Trading Halt)`, null);
      } else if (actionCard.targetType === "ownership") {
        if (actionCard.actionType === "forced_sale") {
          setActionMode("selecting_forced_sale");
          addNotification(`Select another player's ownership token to force them to sell at sell rate`, null);
        }
      } else if (actionCard.targetType === "none") {
        executeActionCard(actionCard);
      }
    }
  };

  const handleDrawActionCard = () => {
    const myActions = actionsRemaining[name] || 0;
    if (!myTurn || myActions <= 0) {
      showMessage("❌ You need actions remaining to draw a card");
      return;
    }
    
    if (playerActionCards.length >= maxActionCards) {
      showMessage(`❌ Your hand is full! You can only hold ${maxActionCards} action cards.`);
      return;
    }
    
    socket.current.emit("draw_action_card", {
      room: room,
      playerName: name
    });
  };

  const handleEnterRedrawMode = useCallback(() => {
    if (!myTurn || (actionsRemaining[name] || 0) <= 0) {
      showMessage("❌ You need actions remaining to discard & redraw");
      return;
    }
    if (hasUsedRedrawThisTurn) {
      showMessage("❌ You can only discard & redraw once per turn");
      return;
    }
    if (playerActionCards.length === 0) {
      showMessage("❌ You have no cards to discard");
      return;
    }
    setSelectedActionCard(null);
    setActionMode(null);
    setRedrawMode(true);
    setSelectedForDiscard(new Set());
    addNotification("Select 1-3 cards to discard and redraw", null);
  }, [myTurn, actionsRemaining, name, hasUsedRedrawThisTurn, playerActionCards.length, showMessage, addNotification]);

  const handleToggleDiscardSelection = useCallback((actionCard) => {
    if (!redrawMode) return;
    setSelectedForDiscard(prev => {
      const updated = new Set(prev);
      if (updated.has(actionCard.id)) {
        updated.delete(actionCard.id);
      } else if (updated.size < 3) {
        updated.add(actionCard.id);
      } else {
        showMessage("❌ You can only discard up to 3 cards");
      }
      return updated;
    });
  }, [redrawMode, showMessage]);

  const handleCancelRedraw = useCallback(() => {
    setRedrawMode(false);
    setSelectedForDiscard(new Set());
  }, []);

  const handleConfirmRedraw = useCallback(() => {
    if (selectedForDiscard.size === 0) {
      showMessage("❌ Select at least 1 card to discard");
      return;
    }
    socket.current.emit("discard_and_redraw", {
      room: room,
      playerName: name,
      cardIds: Array.from(selectedForDiscard)
    });
    setRedrawMode(false);
    setSelectedForDiscard(new Set());
    setHasUsedRedrawThisTurn(true);
  }, [selectedForDiscard, socket, room, name, showMessage]);

  return {
    selectedActionCard,
    actionMode,
    setSelectedActionCard,
    setActionMode,
    pendingTransferSource,
    setPendingTransferSource,
    handleSelectActionCard,
    executeActionCard,
    handleDrawActionCard,
    redrawMode,
    selectedForDiscard,
    hasUsedRedrawThisTurn,
    handleEnterRedrawMode,
    handleToggleDiscardSelection,
    handleCancelRedraw,
    handleConfirmRedraw
  };
};
